'use client';

import React from 'react';
import { useTrackingCode } from 'react-hubspot-tracking-code-hook';

import RenderBodyCopy from '@src/components/Shared/RenderBodyCopy';
import RenderButtons from '@src/components/Shared/RenderButtons';
import Container from '@src/components/Shared/Container';
import SectionHeader from '@src/components/Shared/SectionHeader';

import CallToAction from '@src/components/Sections/CallToAction';
import ImageTextSecondary from '@src/components/Sections/ImageTextSecondary';

import usePreviewContent from '@src/hooks/usePreviewContent';

import constructURL from '@src/utils/constructURL';

import { NormsPageClientPropTypes } from './NormsPage.types';
import NextImage from '@src/components/Shared/NextImage';
import Testimonials from '@src/components/Sections/Testimonials';
import NextLink from '@src/components/Shared/NextLink';

const NormsPageClient: React.FC<NormsPageClientPropTypes> = ({ data }) => {
  // Enable live updates for the post
  const page = usePreviewContent({ content: data?.page });

  // Set the page view path for analytics tracking
  const { setPathPageView, setContentType } = useTrackingCode();
  setPathPageView(
    constructURL({
      languageCode: page?.languageCode?.toLowerCase(),
      slug: `/products/norms`,
      baseURL: '',
    })
  );
  setContentType('site-page');

  return (
    <>
      <Container
        background='bg-[#F8F8F8]'
        id={page?.hero?.items?.[0]?.scrollTo?.slice(1)}
        className='3xl:py-[20rem] flex flex-col py-[6rem] md:flex-row md:items-center md:gap-[3.2rem] md:py-[13rem]'
      >
        <div className='w-full md:relative md:z-20 md:w-[50%]'>
          <NextImage
            className='mx-auto mb-[8rem] max-w-[25rem] md:mx-[unset] md:max-w-[34.6971rem]'
            src={page?.hero?.items?.[0]?.logo?.url}
            alt={page?.hero?.items?.[0]?.logo?.description}
            aspectRatio={
              page?.hero?.items?.[0]?.logo?.width / page?.hero?.items?.[0]?.logo?.height
            }
            width={page?.hero?.items?.[0]?.logo?.width}
          />

          <SectionHeader
            className='md:relative md:z-5'
            title={page?.hero?.items?.[0]?.title}
            preTitleClassName='mb-[1.6rem]'
            titleClassName='mb-[1.8rem] text-[2.8rem] leading-[3.2rem] md:mb-[3.2rem] md:text-[4.8rem] md:leading-[5rem] md:tracking-[-0.096rem]'
            childrenClassName='relative'
            align='left'
          >
            <RenderBodyCopy
              className='max-w-[52rem] leading-[3.36rem] font-light tracking-[-0.0342rem] sm:mx-auto sm:text-center md:mx-[unset] md:max-w-[44.5rem] md:text-left md:leading-[3rem] md:tracking-[-0.036rem] lg:max-w-[48.5rem] lg:text-[2.5rem] lg:leading-[3.6rem] [&>p]:text-[2.1rem]!'
              bodyCopy={page?.hero?.items?.[0]?.bodyCopy?.json?.content}
            />
          </SectionHeader>
        </div>

        <div className='relative z-[-1] mt-[-6rem] w-full scale-[1.4] md:w-[50%] md:scale-[1] lg:bottom-[-3rem] lg:left-[-5rem] lg:mt-[unset] lg:scale-[1.7]'>
          <NextImage
            className='hidden w-full md:block'
            src={page?.hero?.items?.[0]?.mediaPrimary?.url}
            alt={page?.hero?.items?.[0]?.mediaPrimary?.description}
            aspectRatio={
              page?.hero?.items?.[0]?.mediaPrimary?.width / page?.hero?.items?.[0]?.mediaPrimary?.height
            }
            width={page?.hero?.items?.[0]?.mediaPrimary?.width}
          />

          <NextImage
            className='w-full md:hidden'
            src={page?.hero?.items?.[0]?.mediaSecondary?.url}
            alt={page?.hero?.items?.[0]?.mediaSecondary?.description}
            aspectRatio={
              page?.hero?.items?.[0]?.mediaSecondary?.width /
              page?.hero?.items?.[0]?.mediaSecondary?.height
            }
            width={page?.hero?.items?.[0]?.mediaSecondary?.width}
          />
        </div>
      </Container>

      <Container
        id={page?.nextStep?.items?.[0]?.scrollTo?.slice(1)}
        className='flex flex-col lg:py-[4rem]'
      >
        <SectionHeader
          className='order-2 justify-center md:order-1 lg:pb-[unset]'
          pretitle={page?.nextStep?.items?.[0]?.preTitle}
          title={page?.nextStep?.items?.[0]?.title}
          childrenClassName='max-w-[100rem]!'
          titleClassName='text-[2.8rem] lg:text-[4.8rem] mb-[2.4rem] leading-[3.2rem] lg:leading-[4.6rem] xl:leading-[5rem] font-light'
          align='center'
        >
          <>
            <RenderBodyCopy
              className='md:max-w-[100rem]'
              bodyCopy={page?.nextStep?.items?.[0]?.bodyCopy?.json?.content}
            />
            <RenderButtons
              className='md:justify-center'
              items={page?.nextStep?.items?.[0]?.buttonCollection?.items}
            />
          </>
        </SectionHeader>

        <NextImage
          className='order-1 mt-[2.4rem] mb-[4.2rem] md:order-2 md:mt-[7.4rem] md:mb-[0]'
          src={page?.nextStep?.items?.[0]?.mediaPrimary?.url}
          alt={page?.nextStep?.items?.[0]?.mediaPrimary?.description}
          aspectRatio={
            page?.nextStep?.items?.[0]?.mediaPrimary?.width /
            page?.nextStep?.items?.[0]?.mediaPrimary?.height
          }
          width={page?.nextStep?.items?.[0]?.mediaPrimary?.width}
        />
      </Container>

      <ImageTextSecondary
        className='overflow-visible! py-[6rem] lg:gap-[12rem]! lg:py-[9rem]'
        background='bg-[#F8F8F8]'
        id={page?.billionsOfDataPoints?.items?.[0]?.scrollTo?.slice(1)}
        pretitle={page?.billionsOfDataPoints?.items?.[0]?.preTitle}
        title={page?.billionsOfDataPoints?.items?.[0]?.title}
        imgURL={page?.billionsOfDataPoints?.items?.[0]?.mediaPrimary?.url}
        imgAlt={page?.billionsOfDataPoints?.items?.[0]?.mediaPrimary?.description ?? ''}
        titleClassName='max-w-[43rem]'
        childrenElements={
          <>
            <RenderBodyCopy
              bodyCopy={page?.billionsOfDataPoints?.items?.[0]?.bodyCopy?.json?.content}
            />
          </>
        }
      />

      <Container
        id={page?.dataLakehouse?.items?.[0]?.scrollTo?.slice(1)}
        className='flex flex-col lg:py-[4rem]'
      >
        <SectionHeader
          className='order-1 justify-center lg:pb-[unset]'
          pretitle={page?.dataLakehouse?.items?.[0]?.preTitle}
          title={page?.dataLakehouse?.items?.[0]?.title}
          childrenClassName='max-w-[100rem]!'
          titleClassName='text-[2.8rem] lg:text-[4.8rem] mb-[2.4rem] leading-[3.2rem] lg:leading-[4.6rem] xl:leading-[5rem] font-light'
          align='center'
        >
          <>
            <RenderBodyCopy
              className='md:max-w-[100rem]'
              bodyCopy={page?.dataLakehouse?.items?.[0]?.bodyCopy?.json?.content}
            />
            <RenderButtons
              className='md:justify-center'
              items={page?.dataLakehouse?.items?.[0]?.buttonCollection?.items}
            />
          </>
        </SectionHeader>

        <NextImage
          className='order-1 mt-[4.2rem] hidden md:order-2 md:mt-[7.4rem] md:mb-[0] md:block'
          src={page?.dataLakehouse?.items?.[0]?.mediaPrimary?.url}
          alt={page?.dataLakehouse?.items?.[0]?.mediaPrimary?.description}
          aspectRatio={
            page?.dataLakehouse?.items?.[0]?.mediaPrimary?.width /
            page?.dataLakehouse?.items?.[0]?.mediaPrimary?.height
          }
          width={page?.dataLakehouse?.items?.[0]?.mediaPrimary?.width}
        />
        <NextImage
          className='order-1 mt-[4.2rem] md:mt-[7.4rem] md:mb-[0] md:hidden'
          src={page?.dataLakehouse?.items?.[0]?.mediaSecondary?.url}
          alt={page?.dataLakehouse?.items?.[0]?.mediaSecondary?.description}
          aspectRatio={
            page?.dataLakehouse?.items?.[0]?.mediaSecondary?.width /
            page?.dataLakehouse?.items?.[0]?.mediaSecondary?.height
          }
          width={page?.dataLakehouse?.items?.[0]?.mediaSecondary?.width}
        />
      </Container>

      <Container
        id={page?.qualityOut?.items?.[0]?.scrollTo?.slice(1)}
        className='flex flex-col overflow-visible! py-[6rem] lg:py-[9rem]'
        background='bg-[#F8F8F8]'
      >
        <SectionHeader
          className='mb-[4.2rem] justify-center md:mb-[6.6rem] lg:pb-[unset]'
          pretitle={page?.qualityOut?.items?.[0]?.preTitle}
          title={page?.qualityOut?.items?.[0]?.title}
          childrenClassName='max-w-[100rem]!'
          titleClassName='text-[2.8rem] lg:text-[4.8rem] mb-[2.4rem] leading-[3.2rem] lg:leading-[4.6rem] xl:leading-[5rem] font-light'
          align='center'
        >
          <>
            <RenderBodyCopy
              className='md:max-w-[100rem]'
              bodyCopy={page?.qualityOut?.items?.[0]?.bodyCopy?.json?.content}
            />
          </>
        </SectionHeader>

        <NextImage
          className='mx-auto w-[100%] max-w-[71.7rem]'
          src={page?.qualityOut?.items?.[0]?.mediaPrimary?.url}
          alt={page?.qualityOut?.items?.[0]?.mediaPrimary?.description}
          aspectRatio={
            page?.qualityOut?.items?.[0]?.mediaPrimary?.width /
            page?.qualityOut?.items?.[0]?.mediaPrimary?.height
          }
          width={page?.qualityOut?.items?.[0]?.mediaPrimary?.width}
        />
      </Container>

      <Container
        id={page?.yourExpertise?.items?.[0]?.scrollTo?.slice(1)}
        className={`flex flex-col gap-[4.2rem] lg:flex-row lg:items-center`}
      >
        <div className={`order-2 lg:relative lg:z-20 lg:order-1 lg:w-[50%]`}>
          <SectionHeader
            className={`lg:relative lg:z-5 lg:py-[10rem]`}
            pretitle={page?.yourExpertise?.items?.[0]?.preTitle}
            title={page?.yourExpertise?.items?.[0]?.title}
            preTitleClassName='mb-[1.6rem]'
            titleClassName={`text-[2.8rem] leading-[3.2rem] mb-[1.8rem] lg:mb-[3.2rem] lg:text-[4.8rem] lg:leading-[5rem] lg:tracking-[-0.096rem]`}
            childrenClassName='relative'
            align='left'
          >
            <RenderBodyCopy bodyCopy={page?.yourExpertise?.items?.[0]?.bodyCopy?.json?.content} />
          </SectionHeader>
        </div>

        {/* TODO Swap to VideoEmbedPlayer component once training mode vide is update */}
        <div className='relative order-1 aspect-video w-full overflow-hidden lg:relative lg:z-20 lg:order-2 lg:h-[50rem] lg:w-[50%]'>
          <iframe
            className='absolute inset-0 block h-full w-full border-0'
            src={`${page?.yourExpertise?.items?.[0]?.extraAssetsCollection?.items?.[0]?.embedUrl}${page?.yourExpertise?.items?.[0]?.extraAssetsCollection?.items?.[0]?.embedUrl?.includes('?') ? '&' : '?'}background=1&loop=1&autoplay=1&muted=1&badge=0&autopause=0&player_id=0&app_id=58479`}
            allowFullScreen
            allow='autoplay; fullscreen; picture-in-picture'
            title='Video player'
          />
        </div>
      </Container>

      <Container
        id={page?.context?.items?.[0]?.scrollTo?.slice(1)}
        className='mb-[0]! pt-[6rem] pb-[0]'
        background=''
        bgStyle={{
          backgroundImage: `url(${page?.context?.items?.[0]?.mediaPrimary?.url})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <SectionHeader
          className='mb-[2.4rem] h-full sm:items-center! sm:justify-center! sm:text-center! md:mb-[5.6rem]'
          pretitle={page?.context?.items?.[0]?.preTitle}
          title={page?.context?.items?.[0]?.title}
          preTitleClassName='mb-[1.6rem]'
          titleClassName='text-left sm:text-center xl:locale-fr:max-w-[94rem] xl:locale-es:max-w-[79rem] text-[2.8rem] leading-[2.6rem] tracking-[-0.0418] font-light md:leading-[3rem] lg:max-w-[70rem] lg:text-[4.8rem] lg:leading-[5rem]'
          childrenClassName='relative max-w-[99.7rem]!'
          align='left'
        >
          <RenderBodyCopy
            className='mx-auto mt-[2.4rem] text-left sm:mx-auto sm:text-center xl:max-w-full! 2xl:mt-[3.2rem] [&_a]:text-[#000] [&_a]:text-shadow-none!'
            bodyCopy={page?.context?.items?.[0]?.bodyCopy?.json?.content}
          />
          <RenderButtons
            className='justify-center'
            items={page?.context?.items?.[0]?.buttonCollection?.items}
          />
        </SectionHeader>

        <div className='mx-auto max-w-[55rem] overflow-hidden rounded-[1.6rem] md:mb-[-5rem] md:max-w-[124.4rem]'>
          <NextImage
            className='h-auto w-full'
            src={page?.context?.items?.[0]?.mediaSecondary?.url}
            alt=''
            aspectRatio={
              page?.context?.items?.[0]?.mediaSecondary?.width /
              page?.context?.items?.[0]?.mediaSecondary?.height
            }
            width={page?.context?.items?.[0]?.mediaSecondary?.width}
          />
        </div>
      </Container>

      <ImageTextSecondary
        reversed
        className='overflow-visible! py-[6rem] lg:gap-[12rem]! lg:py-[9rem]'
        background='bg-[#F8F8F8]'
        id={page?.betterOutcomes?.items?.[0]?.scrollTo?.slice(1)}
        pretitle={page?.betterOutcomes?.items?.[0]?.preTitle}
        title={page?.betterOutcomes?.items?.[0]?.title}
        imgURL={page?.betterOutcomes?.items?.[0]?.mediaPrimary?.url}
        imgAlt={page?.betterOutcomes?.items?.[0]?.mediaPrimary?.description ?? ''}
        titleClassName='max-w-[43rem]'
        sectionHeaderClassName='max-w-[50rem]'
        childrenElements={
          <>
            <RenderBodyCopy bodyCopy={page?.betterOutcomes?.items?.[0]?.bodyCopy?.json?.content} />
          </>
        }
      />

      <Testimonials
        id={page?.testimonials?.items?.[0]?.scrollTo?.slice(1)}
        testimonials={page?.testimonials?.items?.[0]?.topicCollection?.items}
      />

      <Container
        id={page?.rangeOfPopulations?.items?.[0]?.scrollTo?.slice(1)}
        className='relative py-[6rem] md:flex md:flex-col lg:py-[9rem]'
        background='bg-[#EDEDED] h-[52%] md:h-full'
      >
        <SectionHeader
          className='mb-[2.4rem] justify-center md:mb-[6.6rem] lg:pb-[unset]'
          pretitle={page?.rangeOfPopulations?.items?.[0]?.preTitle}
          title={page?.rangeOfPopulations?.items?.[0]?.title}
          childrenClassName='max-w-[100rem]!'
          titleClassName='text-[2.8rem] lg:text-[4.8rem] mb-[2.4rem] leading-[3.2rem] lg:leading-[4.6rem] xl:leading-[5rem] font-light'
          align='center'
        >
          <>
            <RenderBodyCopy
              className='md:max-w-[100rem]'
              bodyCopy={page?.rangeOfPopulations?.items?.[0]?.bodyCopy?.json?.content}
            />
          </>
        </SectionHeader>

        <div className='flex flex-col gap-[2.4rem] md:flex-row md:gap-[6.4rem]'>
          <NextImage
            src={page?.rangeOfPopulations?.items?.[0]?.mediaPrimary?.url}
            alt={page?.rangeOfPopulations?.items?.[0]?.mediaPrimary?.description}
            aspectRatio={
              page?.rangeOfPopulations?.items?.[0]?.mediaPrimary?.width /
              page?.rangeOfPopulations?.items?.[0]?.mediaPrimary?.height
            }
            width={page?.rangeOfPopulations?.items?.[0]?.mediaPrimary?.width}
          />
          <NextImage
            src={page?.rangeOfPopulations?.items?.[0]?.mediaSecondary?.url}
            alt={page?.rangeOfPopulations?.items?.[0]?.mediaSecondary?.description}
            aspectRatio={
              page?.rangeOfPopulations?.items?.[0]?.mediaSecondary?.width /
              page?.rangeOfPopulations?.items?.[0]?.mediaSecondary?.height
            }
            width={page?.rangeOfPopulations?.items?.[0]?.mediaSecondary?.width}
          />
        </div>
      </Container>

      <Container
        id={page?.norms?.items?.[0]?.scrollTo?.slice(1)}
        className='md:flex md:flex-col lg:py-[4rem]'
      >
        <SectionHeader
          className='mb-[2.4rem] justify-center md:mb-[6.6rem] lg:pb-[unset]'
          pretitle={page?.norms?.items?.[0]?.preTitle}
          title={page?.norms?.items?.[0]?.title}
          childrenClassName='max-w-[100rem]!'
          titleClassName='text-[2.8rem] lg:text-[4.8rem] mb-[2.4rem] leading-[3.2rem] lg:leading-[4.6rem] xl:leading-[5rem] font-light'
          align='center'
        >
          <>
            <RenderBodyCopy
              className='md:max-w-[100rem]'
              bodyCopy={page?.norms?.items?.[0]?.bodyCopy?.json?.content}
            />
          </>
        </SectionHeader>

        <div className='flex flex-col gap-[2.4rem] md:flex-row md:gap-[6.4rem]'>
          <NextLink href='https://valdhealth.com/news/introducing-vald-norms'>
            <NextImage
              className='rounded-[5rem] shadow-[0_4px_24px_0_rgba(0,0,0,0.25)]'
              src={page?.norms?.items?.[0]?.mediaPrimary?.url}
              alt={page?.norms?.items?.[0]?.mediaPrimary?.description}
              aspectRatio={
                page?.norms?.items?.[0]?.mediaPrimary?.width /
                page?.norms?.items?.[0]?.mediaPrimary?.height
              }
              width={page?.norms?.items?.[0]?.mediaPrimary?.width}
            />
          </NextLink>

          <NextLink href='https://valdperformance.com/news/what-are-norms-and-normative-data'>
            <NextImage
              className='rounded-[5rem] shadow-[0_4px_24px_0_rgba(0,0,0,0.25)]'
              src={page?.norms?.items?.[0]?.mediaSecondary?.url}
              alt={page?.norms?.items?.[0]?.mediaSecondary?.description}
              aspectRatio={
                page?.norms?.items?.[0]?.mediaSecondary?.width /
                page?.norms?.items?.[0]?.mediaSecondary?.height
              }
              width={page?.norms?.items?.[0]?.mediaSecondary?.width}
            />
          </NextLink>
        </div>
      </Container>

      <CallToAction
        attr='call-to-action-component'
        id={page?.callToAction?.items?.[0]?.scrollTo?.slice(1)}
        title={page?.callToAction?.items?.[0]?.title}
        bodyCopy={page?.callToAction?.items?.[0]?.bodyCopy?.json?.content}
        buttons={page?.callToAction?.items?.[0]?.buttonCollection?.items}
      />
    </>
  );
};

export default NormsPageClient;
